import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { organizationServiceContract } from "@common/contracts/organization-service.contract";
import {
    OrganizationService,
    OrganizationServiceSchema,
} from "@common/types/organizationService";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const organizationServiceClient = getBackendAPIClient(
    organizationServiceContract,
);

const organizationServiceQueryKeys = {
    all: () => ["organization-service"] as const,
    organization: (organizationId: string) =>
        [
            ...organizationServiceQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    list: (organizationId: string) =>
        [
            ...organizationServiceQueryKeys.organization(organizationId),
            "list",
        ] as const,
};

export function useOrganizationServices() {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? organizationServiceQueryKeys.list(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [] as OrganizationService[];
            }

            const response =
                await organizationServiceClient.listOrganizationServices({
                    params: { organizationId },
                });

            return response.body.services.map((service) =>
                OrganizationServiceSchema.parse(service),
            );
        },
        enabled: !!organizationId,
    });
}

export function useCreateOrganizationServiceMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof organizationServiceContract.createOrganizationService
            >["body"],
        ) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response =
                await organizationServiceClient.createOrganizationService({
                    params: { organizationId },
                    body,
                });

            return OrganizationServiceSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!organizationId) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey:
                    organizationServiceQueryKeys.organization(organizationId),
                exact: false,
            });
        },
    });
}

export function useUpdateOrganizationServiceMutation(
    service?: OrganizationService,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof organizationServiceContract.updateOrganizationService
            >["body"],
        ) => {
            if (!service) {
                throw new Error("No service selected");
            }

            const response =
                await organizationServiceClient.updateOrganizationService({
                    params: {
                        organizationId: service.organizationId,
                        serviceId: service.id,
                    },
                    body,
                });

            return OrganizationServiceSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!service) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey: organizationServiceQueryKeys.organization(
                    service.organizationId,
                ),
                exact: false,
            });
        },
    });
}

export function useDeleteOrganizationServiceMutation(
    service?: OrganizationService,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!service) {
                throw new Error("No service selected");
            }

            await organizationServiceClient.deleteOrganizationService({
                params: {
                    organizationId: service.organizationId,
                    serviceId: service.id,
                },
            });
        },
        onSuccess: async () => {
            if (!service) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey: organizationServiceQueryKeys.organization(
                    service.organizationId,
                ),
                exact: false,
            });
        },
    });
}
